<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'

const page = usePage()
const locale = computed(() => page.props.locale || 'en')
const isRtl = computed(() => locale.value === 'ar')
</script>

<template>
    <section class="relative py-16 lg:py-24 bg-white overflow-hidden" :dir="isRtl ? 'rtl' : 'ltr'">
        <!-- Section 1: Integrated Facilities (Header Block) -->
        <div class="max-w-7xl mx-auto px-4 mb-16">
            <div
                class="relative bg-[#001A5E] rounded-[3rem] overflow-hidden shadow-2xl group min-h-[400px] flex items-center">
                <!-- Background Image with Overlay -->
                <img src="/assets/almatin/factory_production.png"
                    class="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-1000"
                    alt="Factory Production">
                <div class="absolute inset-0 bg-gradient-to-r from-[#001A5E] via-[#001A5E]/80 to-transparent"></div>

                <div class="relative z-10 p-8 md:p-16 max-w-2xl space-y-6">
                    <h2 class="text-4xl md:text-5xl font-black text-white leading-tight">
                        {{ __t('integrated_facilities_title') }}
                    </h2>
                    <p class="text-blue-100 text-lg md:text-xl leading-relaxed opacity-90 font-medium">
                        {{ __t('integrated_facilities_desc') }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Section 2 & 3: Facilities Grid Cards -->
        <div class="max-w-7xl mx-auto px-4 grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
            <!-- Maintenance Workshops -->
            <div
                class="bg-gradient-to-b from-blue-50 to-white p-8 rounded-[2.5rem] border border-blue-100/50 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group">
                <div
                    class="w-16 h-16 bg-[#0044FF] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform duration-500">
                    <v-icon color="white" size="32">mdi-tools</v-icon>
                </div>
                <h3 class="text-2xl font-black text-[#001A5E] mb-4">{{ __t('maintenance_workshops_title') }}</h3>
                <p class="text-gray-600 leading-relaxed">{{ __t('maintenance_workshops_desc') }}</p>
            </div>

            <!-- Quality Labs -->
            <div
                class="relative bg-white p-8 rounded-[2.5rem] border border-blue-100/50 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group overflow-hidden">
                <img src="/assets/almatin/11 (10).webp"
                    class="absolute bottom-0 right-0 w-32 opacity-10 group-hover:opacity-20 transition-opacity"
                    alt="Quality Test">
                <div class="relative z-10">
                    <div
                        class="w-16 h-16 bg-[#FACC15] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-yellow-200 group-hover:scale-110 transition-transform duration-500">
                        <v-icon color="blue-darken-4" size="32">mdi-shield-check</v-icon>
                    </div>
                    <h3 class="text-2xl font-black text-[#001A5E] mb-4">{{ __t('quality_labs_title') }}</h3>
                    <p class="text-gray-600 leading-relaxed">{{ __t('quality_labs_desc') }}</p>
                </div>
            </div>

            <!-- Transport Fleet -->
            <div
                class="bg-gradient-to-b from-blue-50 to-white p-8 rounded-[2.5rem] border border-blue-100/50 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group">
                <div
                    class="w-16 h-16 bg-[#0044FF] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform duration-500">
                    <v-icon color="white" size="32">mdi-truck-cargo-container</v-icon>
                </div>
                <h3 class="text-2xl font-black text-[#001A5E] mb-4">{{ __t('transport_fleet_title') }}</h3>
                <p class="text-gray-600 leading-relaxed">{{ __t('transport_fleet_desc') }}</p>
            </div>

            <!-- Sustainable Energy -->
            <div
                class="bg-gradient-to-b from-blue-50 to-white p-8 rounded-[2.5rem] border border-blue-100/50 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group">
                <div
                    class="w-16 h-16 bg-[#FACC15] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-yellow-200 group-hover:scale-110 transition-transform duration-500">
                    <v-icon color="blue-darken-4" size="32">mdi-solar-power-variant</v-icon>
                </div>
                <h3 class="text-2xl font-black text-[#001A5E] mb-4">{{ __t('sustainable_energy_title') }}</h3>
                <p class="text-gray-600 leading-relaxed">{{ __t('sustainable_energy_desc') }}</p>
            </div>
        </div>

        <!-- Section 4: Global Markets -->
        <div class="bg-gradient-to-r from-[#001A5E] to-[#0033AD] py-20 overflow-hidden relative">
            <div
                class="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/world-map.png')] bg-center bg-no-repeat bg-contain">
            </div>

            <div class="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center relative z-10">
                <div class="space-y-8">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-1 bg-yellow-400"></div>
                        <h2 class="text-3xl md:text-5xl font-black text-white">
                            {{ __t('towards_global_markets_title') }}
                        </h2>
                    </div>

                    <div class="space-y-10">
                        <div class="flex gap-6 items-start">
                            <div class="w-1.5 h-12 bg-yellow-400 shrink-0 mt-1"></div>
                            <p class="text-blue-50 text-xl font-bold leading-relaxed">{{ __t('global_markets_point1') }}
                            </p>
                        </div>
                        <div class="flex gap-6 items-start">
                            <div class="w-1.5 h-12 bg-yellow-400 shrink-0 mt-1"></div>
                            <p class="text-blue-50 text-xl font-bold leading-relaxed">{{ __t('global_markets_point2') }}
                            </p>
                        </div>
                        <div class="flex gap-6 items-start">
                            <div class="w-1.5 h-12 bg-yellow-400 shrink-0 mt-1"></div>
                            <p class="text-blue-50 text-xl font-bold leading-relaxed">{{ __t('global_markets_point3') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Globe Area -->
                <div class="relative flex justify-center items-center">
                    <div class="w-full max-w-lg aspect-square bg-[#0044FF]/20 rounded-full blur-[100px] absolute"></div>
                    <div class="relative z-10">
                        <!-- <v-icon size="300" color="white" class="opacity-30">mdi-earth</v-icon> -->
                        <div class=" inset-0 flex items-center justify-center">
                            <img src="/assets/almatin/11 (11).png"
                                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                                alt="Al Matin Partnership">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Section 5: Partnership & Social Responsibility -->
        <div class="max-w-7xl mx-auto px-4 pt-24">
            <div class="bg-gray-50/50 rounded-[4rem] p-10 md:px-16 py-16 border border-gray-100 shadow-sm">
                <div class="text-center mb-16">
                    <h2 class="text-4xl md:text-5xl font-black text-[#001A5E] mb-6">
                        {{ __t('partnership_development_title') }}
                    </h2>
                </div>

                <div class="grid lg:grid-cols-2 gap-16 items-center">
                    <div class="space-y-10">
                        <div class="flex gap-8 items-start">
                            <div
                                class="w-20 h-20 bg-blue-100 rounded-3xl flex items-center justify-center shrink-0 shadow-inner">
                                <v-icon color="#0033AD" size="40">mdi-handshake</v-icon>
                            </div>
                            <p class="text-gray-700 text-lg leading-relaxed font-medium">
                                {{ __t('partnership_development_desc1') }}
                            </p>
                        </div>

                        <div class="flex gap-8 items-start">
                            <div
                                class="w-20 h-20 bg-yellow-100 rounded-3xl flex items-center justify-center shrink-0 shadow-inner">
                                <v-icon color="#B45309" size="40">mdi-account-group</v-icon>
                            </div>
                            <p class="text-gray-700 text-lg leading-relaxed font-medium">
                                {{ __t('partnership_development_desc2') }}
                            </p>
                        </div>
                    </div>

                    <div class="relative rounded-[3rem] overflow-hidden aspect-[4/3] shadow-2xl group">
                        <img src="/assets/almatin/1 (1).webp"
                            class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                            alt="Al Matin Partnership">
                        <div class="absolute inset-0 bg-gradient-to-t from-[#001A5E]/60 to-transparent"></div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.animate-pulse-slow {
    animation: pulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.8;
        transform: scale(1.05);
    }
}
</style>
